/* Copyright Verge Technologies 2018
 * @file productFilters.js
 * @author Brenton Watt <brenton@verge.co.za>
 * @description Component dealing with product filtering
 */

// Modules
import React, { Component } from 'react';
import { PulseLoader } from 'react-spinners';
import { withApollo, compose, graphql } from 'react-apollo';
import CheckboxTree from 'react-checkbox-tree';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckSquare, faMinusSquare, faChevronRight, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { faSquare } from '@fortawesome/free-regular-svg-icons';
// GraphQL
import { APPLICATIONS_QUERY, BRANDS_QUERY } from '../../graphql';
import { retrieveFromHash, clearQueryString } from '../../common/helper';

class ProductFilters extends Component {
  constructor(props) {
    super(props);
    this.state = {
      checked: [],
      expanded: [],
      loading: true,
      nodes: [],
      preselected: false,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.isBrand && !nextProps.brandsError && !nextProps.brandsLoading) {
      const structure = this.treeSetup(nextProps.brands);
      if (!this.state.preselected || this.props.id !== nextProps.id || (!this.state.preselected && nextProps.id && this.props.id === nextProps.id && nextProps.checkedBrands.length === 0)) {
        this.setState({ preselected: true });
      }
      const { checkedBrands } = nextProps;
      const nodes = structure;
      const flattenChildren = (node, parent_short_ids) => {
        return [{ short_id: `${node.short_id}`, id: node.id, name: node.name, parent_short_ids }, ...node.children.flatMap(child => {
            if (child.children && child.children.length) {
              return [{ short_id: `${child.short_id}`, parent_short_ids: [`${node.short_id}`], id: child.id, name: child.name }, ...child.children.flatMap(c => flattenChildren(c, [`${child.short_id}`, `${node.short_id}`]))];
            }
            return { short_id: `${child.short_id}`, parent_short_ids: [`${node.short_id}`], id: child.id, name: child.name };
          })];
      };
      const flattenNodes = nodes.flatMap(node => flattenChildren(node));
      const expandedNodes = [...new Set(flattenNodes.filter(node => checkedBrands.includes(node.short_id)).flatMap(node => node.parent_short_ids).filter(x => x))];
      this.setState({ nodes: structure, loading: false, expanded: expandedNodes });
    } else if (!nextProps.isBrand && !nextProps.applicationsError && !nextProps.applicationsLoading) {
      const structure = this.treeSetup(nextProps.applications);
      if (!this.state.preselected || this.props.id !== nextProps.id || (!this.state.preselected && nextProps.id && this.props.id === nextProps.id && nextProps.checkedApplications.length === 0)) {
        const preselected = this.getPreselectedLeaves(structure, nextProps.id);
        this.setState({ preselected: true });
        this.props.updateApplication([...preselected, ...nextProps.checkedApplications]);
      }
      this.setState({ nodes: structure, loading: false });
    }
  }

  treeSetup = (structure, parent = null) => {
    for (let i = 0; i < structure.length; i++) {
      const value = structure[i].short_id;
      // eslint-disable-next-line
      structure[i].value = structure[i].short_id,
        structure[i].label = `${structure[i].name} (${structure[i].count})`;
      if (structure[i].children.length > 0) {
        this.treeSetup(structure[i].children, value);
      }
    }
    return structure;
  };

  getPreselectedLeaves = (nodes, id) => {
    const selected = [];
    this.getPreselectedRecursive(nodes.find(node => node.cat_id === id), selected);
    return selected;
  }

  getPreselectedRecursive = (node, selected, parent = null) => {
    if (!node) return;
    if (node.children.length > 0) {
      const parentValue = {
        id: node.id,
        cat_id: node.cat_id,
        children: node.children.length,
        parent,
      }
      for (let i = 0; i < node.children.length; i += 1) {

        this.getPreselectedRecursive(node.children[i], selected, parentValue);
      }
    } else {
      const value = {
        id: node.id,
        cat_id: node.cat_id,
        children: node.children.length,
        parent,
      }
      selected.push(JSON.stringify(value));
    }
  }

  getExpandedNodes = (node, expandedNodes = []) => {
    if (!node) return;
    if (node.children && node.children.length > 0) {
      expandedNodes.push(`${node.short_id}`);
      for (let i = 0; i < node.children.length; i += 1) {
        this.getSelectedNodes(node.children[i], expandedNodes);
      }
    }
    return expandedNodes;
  }

  getBrands = (checkboxIcons) => {
    const { nodes, loading } = this.state;
    const { brandsError, brandsLoading } = this.props;
    if (loading || brandsLoading) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100px' }}>
          <PulseLoader loading={loading} color="#004DA9" sizeUnit="px" size={15} />
        </div>
      );
    }
    if (brandsError) {
      console.log(brandsError);
      return <div>Error</div>;
    }

    /**
     * When user selects a brand on a productcard, brand node short id is passed into the hashurl
     * The check is for a single shortId
     * Then a recursive function travese tree and return all short_ids associated with that brand
     */
    const checked = this.props.checkedBrands;
    const expandedNodes = nodes.filter(node => node.children.some(child => checked.includes(`${child.short_id}`))).map(node => node.short_id);
    return (
      <div style={{ maxWidth: '100%' }}>
        <ul className="list">
          <CheckboxTree
            nodes={nodes}
            showNodeIcon={false}
            checked={checked}
            expanded={this.state.expanded}
            onCheck={(checkedArray, targetNode) => {
              /**
               * As top node also includes products we need to include its short code into the array for the query to return all products
               */
              if (targetNode.checked && !(targetNode.children && targetNode.children.length) && !checkedArray.includes(targetNode.value) && !(checked.includes(targetNode.value))) {
                checkedArray.push(targetNode.value);
              } else {
                checkedArray = checkedArray.filter(chck => {
                  chck = parseInt(chck);
                  return (chck !== targetNode.value)
                });
              }
              this.props.updateBrand(checkedArray);
            }}
            onExpand={expanded => { console.log('Expanded', expanded); this.setState({ expanded }); }}
            expandOnClick
            icons={checkboxIcons}
          />
        </ul>
      </div>
    );
  }

  getApplications = (checkboxIcons) => {
    const { nodes, loading } = this.state;
    const { applicationsError, applicationsLoading } = this.props;
    if (loading || applicationsLoading) {
      return (<div style={{ textAlign: 'center', marginTop: 50 }}><PulseLoader loading={loading} color="#004DA9" sizeUnit="px" size={15} /></div>)
    }
    if (applicationsError) {
      console.log(this.props.applicationsError);
      return <div>Error</div>;
    }

    return (
      <div>
        <ul className="list">
          <CheckboxTree
            nodes={nodes}
            showNodeIcon={false}

            checked={this.props.checkedApplications}
            expanded={this.state.expanded}
            onCheck={checked => {
              this.props.updateApplication(checked);
            }}
            onExpand={expanded => { console.log('Expanded', expanded); this.setState({ expanded }); }}
            expandOnClick
            icons={checkboxIcons}
          />
        </ul>
      </div>
    );
  }

  clearSelected = () => {
    if (this.props.isBrand) {
      this.props.updateBrand([]);
    } else {
      this.props.updateApplication([]);
    }
    // clearQueryString()
    this.setState({ expanded: [] })
  }


  render() {
    const checkboxIcons = {
      check: <FontAwesomeIcon icon={faCheckSquare} color="#81D34C" size="lg" />,
      halfCheck: <FontAwesomeIcon icon={faMinusSquare} color="#81D34C" size="lg" />,
      uncheck: <FontAwesomeIcon icon={faSquare} color="#004DA9" size="lg" />,
      expandClose: <FontAwesomeIcon icon={faChevronRight} color="#004DA9" size="1x" />,
      expandOpen: <FontAwesomeIcon icon={faChevronDown} color="#004DA9" size="1x" />,
    };
    return (
      <div className="left-section">
        <div className="panel">
          <div className="panelHeading">
            {this.props.isBrand ? (<h4>Suppliers</h4>) : (<h4>Applications</h4>)}
            <h3 className="search-control" onClick={() => this.clearSelected()}>
              Clear All
            </h3>
          </div>
          {this.props.isBrand ?
            this.getBrands(checkboxIcons)
            :
            this.getApplications(checkboxIcons)
          }
        </div>
      </div>
    );
  }
}

export default compose(withApollo,
  graphql(BRANDS_QUERY, {
    options: {
      fetchPolicy: 'cache-and-network',
    },
    props: (response) => {
      const { loading, error, getBrands } = response.data;
      return ({
        brands: JSON.parse(getBrands || '[]').sort((a, b) => {
          return a.name.localeCompare(b.name);
        }),
        brandsLoading: loading,
        brandsError: error,
      });
    },
  }),
  graphql(APPLICATIONS_QUERY, {
    options: {
      fetchPolicy: 'cache-and-network',
    },
    props: (response) => {
      const { loading, error, getApplications } = response.data;
      return ({
        applications: JSON.parse(getApplications || '[]'),
        applicationsLoading: loading,
        applicationsError: error,
      });
    },
  })
)(ProductFilters);



// WEBPACK FOOTER //
// ./src/components/ProductFilters/productFilters.js